import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { PageLoading } from '@/components/ui';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { FEATURES } from '@/lib/features';

// Layouts (kept eager - they wrap all routes)
import { MainLayout } from '@/layouts/MainLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { DashboardLayout } from '@/layouts/DashboardLayout';

// Public Pages (lazy loaded)
const HomePage = lazy(() => import('@/pages/HomePage').then(m => ({ default: m.HomePage })));
const ExplorePage = lazy(() => import('@/pages/ExplorePage').then(m => ({ default: m.ExplorePage })));
const AssetDetailPage = lazy(() => import('@/pages/AssetDetailPage').then(m => ({ default: m.AssetDetailPage })));
const PublicProfilePage = lazy(() => import('@/pages/PublicProfilePage').then(m => ({ default: m.PublicProfilePage })));
const PricingPage = lazy(() => import('@/pages/PricingPage').then(m => ({ default: m.PricingPage })));

// Institutional Pages (lazy loaded)
const HelpCenterPage = lazy(() => import('@/pages/institutional').then(m => ({ default: m.HelpCenterPage })));
const HelpArticlePage = lazy(() => import('@/pages/institutional').then(m => ({ default: m.HelpArticlePage })));
const ContactPage = lazy(() => import('@/pages/institutional').then(m => ({ default: m.ContactPage })));
const FAQPage = lazy(() => import('@/pages/institutional').then(m => ({ default: m.FAQPage })));
const TermsPage = lazy(() => import('@/pages/institutional').then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('@/pages/institutional').then(m => ({ default: m.PrivacyPage })));

// Auth Pages (lazy loaded)
const LoginPage = lazy(() => import('@/pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage').then(m => ({ default: m.RegisterPage })));
const CompleteSignupPage = lazy(() => import('@/pages/auth/CompleteSignupPage').then(m => ({ default: m.CompleteSignupPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));

// Dashboard Pages (lazy loaded)
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })));
const MyAssetsPage = lazy(() => import('@/pages/dashboard/MyAssetsPage').then(m => ({ default: m.MyAssetsPage })));
const CreateAssetPage = lazy(() => import('@/pages/dashboard/CreateAssetPage').then(m => ({ default: m.CreateAssetPage })));
const SavedSearchesPage = lazy(() => import('@/pages/dashboard/SavedSearchesPage').then(m => ({ default: m.SavedSearchesPage })));
const MyOffersPage = lazy(() => import('@/pages/dashboard/MyOffersPage').then(m => ({ default: m.MyOffersPage })));
const ReceivedProposalsPage = lazy(() => import('@/pages/dashboard/ReceivedProposalsPage').then(m => ({ default: m.ReceivedProposalsPage })));
const NegotiationsPage = lazy(() => import('@/pages/dashboard/NegotiationsPage').then(m => ({ default: m.NegotiationsPage })));
const ProfilePage = lazy(() => import('@/pages/dashboard/ProfilePage').then(m => ({ default: m.ProfilePage })));
const DealsPage = lazy(() => import('@/pages/dashboard/DealsPage').then(m => ({ default: m.DealsPage })));
const SubscriptionPage = lazy(() => import('@/pages/dashboard/SubscriptionPage').then(m => ({ default: m.SubscriptionPage })));
const NotificationsPage = lazy(() => import('@/pages/dashboard/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const StickerCardPage = lazy(() => import('@/pages/dashboard/StickerCardPage').then(m => ({ default: m.StickerCardPage })));
const StickerCardEditorPage = lazy(() => import('@/pages/dashboard/StickerCardEditorPage').then(m => ({ default: m.StickerCardEditorPage })));
const StickerCardAnalyticsPage = lazy(() => import('@/pages/dashboard/StickerCardAnalyticsPage').then(m => ({ default: m.StickerCardAnalyticsPage })));
const MyFeedbackPage = lazy(() => import('@/pages/dashboard/MyFeedbackPage').then(m => ({ default: m.MyFeedbackPage })));
const NovidadesPage = lazy(() => import('@/pages/dashboard/NovidadesPage').then(m => ({ default: m.NovidadesPage })));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const RankingPage = lazy(() => import('@/pages/dashboard/RankingPage').then(m => ({ default: m.RankingPage })));
const ChamadasPage = lazy(() => import('@/pages/ChamadasPage').then(m => ({ default: m.ChamadasPage })));
const ChamadaDetailPage = lazy(() => import('@/pages/ChamadaDetailPage').then(m => ({ default: m.ChamadaDetailPage })));
const PatrocinadoresPage = lazy(() => import('@/pages/PatrocinadoresPage').then(m => ({ default: m.PatrocinadoresPage })));
const PatrocinadorPage = lazy(() => import('@/pages/PatrocinadoresPage').then(m => ({ default: m.PatrocinadorPage })));
const MinhasChamadasPage = lazy(() => import('@/pages/dashboard/SponsorChamadasPages').then(m => ({ default: m.MinhasChamadasPage })));
const ChamadaFormPage = lazy(() => import('@/pages/dashboard/SponsorChamadasPages').then(m => ({ default: m.ChamadaFormPage })));
const ChamadaCandidaturasPage = lazy(() => import('@/pages/dashboard/SponsorChamadasPages').then(m => ({ default: m.ChamadaCandidaturasPage })));
const MinhasCandidaturasPage = lazy(() => import('@/pages/dashboard/MinhasCandidaturasPage').then(m => ({ default: m.MinhasCandidaturasPage })));

// Admin Pages (lazy loaded)
const AdminDashboardPage = lazy(() => import('@/pages/dashboard/admin').then(m => ({ default: m.AdminDashboardPage })));
const AdminUsersPage = lazy(() => import('@/pages/dashboard/admin').then(m => ({ default: m.AdminUsersPage })));
const AdminAssetsPage = lazy(() => import('@/pages/dashboard/admin').then(m => ({ default: m.AdminAssetsPage })));
const AdminKycPage = lazy(() => import('@/pages/dashboard/admin').then(m => ({ default: m.AdminKycPage })));
const AdminAudienceVerificationPage = lazy(() => import('@/pages/dashboard/admin').then(m => ({ default: m.AdminAudienceVerificationPage })));
const AdminReportsPage = lazy(() => import('@/pages/dashboard/admin').then(m => ({ default: m.AdminReportsPage })));
const AdminSeasonsPage = lazy(() => import('@/pages/dashboard/admin').then(m => ({ default: m.AdminSeasonsPage })));
const AdminStickerCardsPage = lazy(() => import('@/pages/dashboard/admin').then(m => ({ default: m.AdminStickerCardsPage })));
const AdminOcorrenciasPage = lazy(() => import('@/pages/dashboard/admin/AdminOcorrenciasPage').then(m => ({ default: m.AdminOcorrenciasPage })));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, pendingGoogleSignup } = useAuthStore();

  if (isLoading) {
    return <PageLoading />;
  }

  if (pendingGoogleSignup) {
    return <Navigate to="/completar-cadastro" replace />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore();

  if (isLoading) {
    return <PageLoading />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoading />}>
        <Routes>
          {/* Public routes */}
          <Route element={<MainLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/asset/:id" element={<AssetDetailPage />} />
            <Route path="/perfil/:userId" element={<PublicProfilePage />} />
            <Route path="/planos" element={<PricingPage />} />
            <Route path="/explorar" element={<ExplorePage />} />
            <Route path="/chamadas" element={<ChamadasPage />} />
            <Route path="/chamadas/:id" element={<ChamadaDetailPage />} />
            <Route path="/patrocinadores" element={<PatrocinadoresPage />} />
            <Route path="/patrocinadores/:userId" element={<PatrocinadorPage />} />

            {/* Institutional pages */}
            <Route path="/ajuda" element={<HelpCenterPage />} />
            <Route path="/ajuda/:slug" element={<HelpArticlePage />} />
            <Route path="/contato" element={<ContactPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/termos" element={<TermsPage />} />
            <Route path="/privacidade" element={<PrivacyPage />} />
            <Route path="/novidades" element={<NovidadesPage />} />
          </Route>

          {/* Auth routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/cadastro" element={<RegisterPage />} />
            <Route path="/recuperar-senha" element={<ForgotPasswordPage />} />
            <Route path="/completar-cadastro" element={<CompleteSignupPage />} />
          </Route>

          {/* Protected dashboard routes */}
          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/dashboard/assets" element={<MyAssetsPage />} />
            <Route path="/dashboard/assets/novo" element={<CreateAssetPage />} />
            <Route path="/dashboard/ofertas" element={<MyOffersPage />} />
            <Route path="/dashboard/buscas" element={<SavedSearchesPage />} />
            <Route path="/dashboard/propostas" element={<ReceivedProposalsPage />} />
            <Route path="/dashboard/negociacoes" element={<NegotiationsPage />} />
            <Route path="/dashboard/acordos" element={<DealsPage />} />
            {FEATURES.stickerCards && (
              <>
                <Route path="/dashboard/criador-de-posts" element={<StickerCardPage />} />
                <Route path="/dashboard/criador-de-posts/novo" element={<StickerCardEditorPage />} />
                <Route path="/dashboard/criador-de-posts/:cardId" element={<StickerCardEditorPage />} />
                <Route path="/dashboard/criador-de-posts/:cardId/analytics" element={<StickerCardAnalyticsPage />} />
              </>
            )}
            {FEATURES.ranking && <Route path="/dashboard/ranking" element={<RankingPage />} />}

            <Route path="/dashboard/assinatura" element={<SubscriptionPage />} />
            <Route path="/dashboard/notificacoes" element={<NotificationsPage />} />
            <Route path="/dashboard/feedback" element={<MyFeedbackPage />} />
            <Route path="/dashboard/novidades" element={<NovidadesPage />} />
            <Route path="/dashboard/perfil" element={<ProfilePage />} />
            <Route path="/dashboard/explorar" element={<ExplorePage />} />
            <Route path="/dashboard/buscar-patrocinio" element={<ChamadasPage />} />
            <Route path="/dashboard/candidaturas" element={<MinhasCandidaturasPage />} />
            <Route path="/dashboard/chamadas" element={<MinhasChamadasPage />} />
            <Route path="/dashboard/chamadas/nova" element={<ChamadaFormPage />} />
            <Route path="/dashboard/chamadas/:id" element={<ChamadaCandidaturasPage />} />
            <Route path="/dashboard/chamadas/:id/editar" element={<ChamadaFormPage />} />
          </Route>

          {/* Admin routes */}
          <Route
            element={
              <AdminRoute>
                <DashboardLayout />
              </AdminRoute>
            }
          >
            <Route path="/dashboard/admin" element={<AdminDashboardPage />} />
            <Route path="/dashboard/admin/usuarios" element={<AdminUsersPage />} />
            <Route path="/dashboard/admin/assets" element={<AdminAssetsPage />} />
            <Route path="/dashboard/admin/kyc" element={<AdminKycPage />} />
            <Route path="/dashboard/admin/audiencia" element={<AdminAudienceVerificationPage />} />
            <Route path="/dashboard/admin/relatorios" element={<AdminReportsPage />} />
            <Route path="/dashboard/admin/ocorrencias" element={<AdminOcorrenciasPage />} />
            {FEATURES.ranking && <Route path="/dashboard/admin/temporadas" element={<AdminSeasonsPage />} />}
            {FEATURES.stickerCards && <Route path="/dashboard/admin/figurinhas" element={<AdminStickerCardsPage />} />}
          </Route>

          {/* Fallback */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
